import React, { ReactNode, useMemo } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { FieldType, ParticipantFieldType } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
  },
  sortable: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      color: theme.colors.inkPrimary,
    },
  },
  sortIcon: {
    ...theme.fonts.caption,
    fontSize: theme.iconSize.small,
    color: theme.colors.blue,
  },
}));

interface Props {
  fieldKey: string;
  fieldType: FieldType | ParticipantFieldType;
  children: ReactNode;
  currentSort?: { field: string; type: string; direction: 'asc' | 'desc' };
  onSortClick?: (fieldKey: string, fieldType: string) => void;
}

export const TableParticipationHeader: React.FC<Props> = ({
  fieldKey,
  fieldType,
  currentSort,
  onSortClick,
  children,
}) => {
  const classes = useStyles();
  const isSortable = useMemo(() => {
    return (
      onSortClick &&
      ![
        FieldType.MULTIPLE_CHOICE,
        ParticipantFieldType.MULTIPLE_CHOICE,
        ParticipantFieldType.TAGS,
        ParticipantFieldType.LOCALES,
      ].includes(fieldType)
    );
  }, [fieldType, onSortClick]);
  const isSorted = isSortable && currentSort?.field === fieldKey;

  return (
    <span
      className={classnames(classes.header, {
        [classes.sortable]: isSortable,
      })}
      onClick={() =>
        isSortable && onSortClick
          ? onSortClick(fieldKey, fieldType.toString())
          : undefined
      }
    >
      {isSorted && (
        <i
          className={classnames(
            classes.sortIcon,
            currentSort.direction === 'asc'
              ? 'ri-arrow-up-line'
              : 'ri-arrow-down-line',
          )}
        />
      )}
      {children}
    </span>
  );
};
